import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { fetchBidIncrementsIfNeeded } from '@/redux/modules/bidIncrements/bidIncrements.actions';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import {
    getCatalogIsCatalogOnly,
    getCatalogIsInstantAutopay,
    getCatalogIsTimedPlus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getRegistrationDeclined,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderMaxBid,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsAvailable,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemDetailsIfNeeded } from '@/redux/modules/itemDetail';
import { fetchItemFacetsIfNeeded } from '@/redux/modules/itemFacets';
import { CompleteAccountModalContext, ModalContext, openPlaceAbsenteeBidModal } from '@/redux/modules/modal';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { getNextBidMinimum } from '@/utils/absenteeBids';
import {
    TrackPlaceBidPressedButtonLocation,
    useTrackPlaceBidPressedAnalytics,
} from '@/utils/amplitude/analytics/bidding/trackPlaceBidPressed.analytics';

type Props = {
    itemId: number;
};

export const CatalogPlaceBidButton = ({ itemId }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    // Get all required data from Redux
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const trackPlaceBidPressed = useTrackPlaceBidPressedAnalytics(catalogId, itemId);
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    // Visibility conditions
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const isDeclined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const isSuspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));

    // Calculate bid amount with state management
    const [bidAmount, setBidAmount] = useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );
    const [stateItemId, setItemId] = useState(itemId || 0);
    const [newBidMinimum, setNewBidMinimum] = useState(0);

    const previousItemId = usePrevious(stateItemId);
    const previousNewBidMinimum = usePrevious(newBidMinimum);

    // Update bid amount when bid environment changes
    useEffect(() => {
        const calculatedBidMinimum =
            getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

        if (previousItemId !== itemId || previousNewBidMinimum !== calculatedBidMinimum) {
            setBidAmount(calculatedBidMinimum);
            setItemId(itemId);
            setNewBidMinimum(calculatedBidMinimum);
        }
    }, [bidIncrements, bidderMaxBid, leadingBid, startPrice, itemId, previousItemId, previousNewBidMinimum]);

    // Modal context
    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    // Click handler
    const handlePlaceBid = useCallback(async () => {
        trackPlaceBidPressed(TrackPlaceBidPressedButtonLocation.ItemCard);
        const actionPromises: Promise<any>[] = [
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchItemFacetsIfNeeded([itemId])),
            dispatch(fetchBidIncrementsIfNeeded(catalogId)),
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] })),
        ];
        await Promise.all(actionPromises);
        dispatch(
            openPlaceAbsenteeBidModal({
                bidAmount,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    }, [dispatch, itemId, catalogId, bidAmount, isInstantAutopay, modalContext, trackPlaceBidPressed]);

    const shouldShowButton = useMemo(() => {
        return (
            !isTimedPlus &&
            !isCatalogOnly &&
            buyNowStatus !== BuyNowStatus.Available &&
            (catalogStatus === AuctionStatus.Upcoming ||
                catalogStatus === AuctionStatus.Online ||
                catalogStatus === AuctionStatus.Live) &&
            isAvailable &&
            !isSold &&
            !isPassed &&
            !isLocked &&
            !isDeclined &&
            !isSuspended
        );
    }, [
        isTimedPlus,
        isCatalogOnly,
        buyNowStatus,
        catalogStatus,
        isAvailable,
        isSold,
        isPassed,
        isLocked,
        isDeclined,
        isSuspended,
    ]);
    // Check visibility conditions - only show for upcoming auction or online auction

    if (!shouldShowButton) {
        // Since now we show this on live auctions, we need to return a span to avoid layout shift
        return <div className="h-lg w-0" />;
    }

    return (
        <Button
            className="mt-xs h-min self-end"
            onClick={handlePlaceBid}
            size="sm"
            variant="outlined"
        >
            {formatMessage({ id: bidderHasBid ? 'catalogPage.bidding.increaseBid' : 'catalogPage.bidding.placeBid' })}
        </Button>
    );
};
